import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { AdminSidebar } from "../components/admin/AdminSidebar";
import { AnalyticsDashboard } from "../components/admin/AnalyticsDashboard";
import { OutboundClicksWidget } from "../components/admin/OutboundClicksWidget";
import { TrackBreakdownWidget } from "../components/admin/TrackBreakdownWidget";
import { LiveListenerTracker } from "../components/admin/LiveListenerTracker";
import { ListeningHeatmapWidget } from "../components/admin/ListeningHeatmapWidget";
import { VisitorBreakdownWidget } from "../components/admin/VisitorBreakdownWidget";
import { DeviceBrowserWidget } from "../components/admin/DeviceBrowserWidget";
import { ConversionFunnelWidget } from "../components/admin/ConversionFunnelWidget";
import { SleepFeatureUsageWidget } from "../components/admin/SleepFeatureUsageWidget";
import { WorldMapWidget } from "../components/admin/WorldMapWidget";
import { ReleaseManagement } from "../components/admin/ReleaseManagement";
import { useAdminAuth } from "../context/AdminAuthContext";
import { api } from "../lib/api";
import { ADMIN } from "../constants/testIds";

export default function AdminDashboard() {
  const { logout } = useAdminAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("overview");
  const [releases, setReleases] = useState([]);
  const [days, setDays] = useState("30");
  const [release, setRelease] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadReleases = useCallback(() => {
    api.get("/admin/releases").then(({ data }) => setReleases(data)).catch(() => setReleases([]));
  }, []);

  useEffect(() => { loadReleases(); }, [loadReleases]);

  useEffect(() => {
    setLoading(true);
    const params = { days };
    if (release) params.release = release;
    api.get("/admin/analytics/overview", { params })
      .then(({ data }) => setAnalytics(data))
      .catch(() => setAnalytics(null))
      .finally(() => setLoading(false));
  }, [days, release]);

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#090D16] text-slate-200 flex" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <AdminSidebar section={section} onSection={setSection} onLogout={onLogout} />
      <main className="flex-1 min-w-0 w-full">
        <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-[#0D1322]">
          <p style={{ fontFamily: "'Outfit', sans-serif" }} className="text-sm font-bold text-slate-50">NINDIYA CONTROL</p>
          <div className="flex items-center gap-2">
            <button data-testid="admin-nav-overview-mobile" onClick={() => setSection("overview")} className={`text-xs px-3 py-1.5 rounded-full ${section === "overview" ? "bg-amber-500/10 text-amber-300 border border-amber-500/30" : "text-slate-400 border border-transparent"}`}>Overview</button>
            <button data-testid="admin-nav-releases-mobile" onClick={() => setSection("releases")} className={`text-xs px-3 py-1.5 rounded-full ${section === "releases" ? "bg-amber-500/10 text-amber-300 border border-amber-500/30" : "text-slate-400 border border-transparent"}`}>Releases</button>
            <button data-testid="admin-logout-button-mobile" onClick={onLogout} className="text-xs px-3 py-1.5 rounded-full text-red-300">Log out</button>
          </div>
        </div>
        <header className="min-h-16 sticky top-0 z-10 bg-[#090D16]/90 backdrop-blur-md border-b border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 sm:px-6 lg:px-8 py-3 sm:py-0">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {section === "overview" ? "Overview" : "Releases"}
          </h1>
          {section === "overview" && (
            <div className="flex flex-wrap gap-3">
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger data-testid={ADMIN.timeframeSelect} className="w-36 bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                </SelectContent>
              </Select>
              <Select value={release || "all"} onValueChange={(v) => setRelease(v === "all" ? "" : v)}>
                <SelectTrigger data-testid={ADMIN.releaseFilterSelect} className="w-48 bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                  <SelectItem value="all">All releases</SelectItem>
                  {releases.map((r) => <SelectItem key={r.slug} value={r.slug}>{r.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </header>

        {section === "overview" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 sm:p-6 lg:p-8 min-w-0">
            <div className="col-span-1 md:col-span-2 lg:col-span-4 min-w-0">
              <AnalyticsDashboard data={analytics} loading={loading} />
            </div>
            <div className="col-span-1 md:col-span-2 min-w-0">
              <ListeningHeatmapWidget data={analytics} loading={loading} />
            </div>
            <div className="col-span-1 md:col-span-2 min-w-0">
              <VisitorBreakdownWidget data={analytics} loading={loading} />
            </div>
            <div className="col-span-1 lg:col-span-2 min-w-0">
              <DeviceBrowserWidget data={analytics} loading={loading} />
            </div>
            <div className="col-span-1 lg:col-span-2 min-w-0">
              <ConversionFunnelWidget data={analytics} loading={loading} />
            </div>
            <div className="col-span-1 lg:col-span-2 min-w-0">
              <SleepFeatureUsageWidget data={analytics} loading={loading} />
            </div>
            <div className="col-span-1 lg:col-span-2 min-w-0">
              <OutboundClicksWidget data={analytics} loading={loading} />
            </div>
            <div className="col-span-1 md:col-span-2 lg:col-span-4 min-w-0">
              <WorldMapWidget data={analytics} loading={loading} />
            </div>
            <div className="col-span-1 md:col-span-2 lg:col-span-4 min-w-0">
              <TrackBreakdownWidget data={analytics} loading={loading} />
            </div>
            <div className="col-span-1 md:col-span-2 lg:col-span-4 min-w-0">
              <LiveListenerTracker releases={releases} />
            </div>
          </div>
        ) : (
          <div className="p-4 sm:p-6 lg:p-8 min-w-0">
            <ReleaseManagement releases={releases} onChanged={loadReleases} />
          </div>
        )}
      </main>
    </div>
  );
}
